<template>
  <b-container v-if="codigo && estado" class="tc-wrap">
    <div v-if="celebrando" class="tc-lluvia" aria-hidden="true">
      <span
        v-for="(c, i) in confetti"
        :key="i"
        class="tc-piece"
        :style="{
          left: c.left,
          background: c.color,
          width: c.ancho,
          height: c.alto,
          borderRadius: c.radio,
          animationDelay: c.delay,
          animationDuration: c.dur,
          '--tc-giro': c.giro,
        }"
      ></span>
    </div>

    <!-- Recién canjeado en esta visita. -->
    <div v-if="celebrando" class="tc-card tc-win">
      <div class="tc-main">
        <div class="tc-emoji">🎟️</div>
        <h3 class="tc-title">¡Listo! Tu regalo ya está en tu cuenta</h3>
        <p class="tc-sub">
          Sumamos <strong>{{ usosLabel }}</strong> de {{ servicio.nombre }}
          <span v-if="cuenta">a la cuenta <strong>{{ cuenta }}</strong></span>
          <span v-else>a tu cuenta</span>. {{ servicio.cierre }}
        </p>
      </div>
    </div>

    <!-- Vivo: quien acaba de abrir el enlace del QR. -->
    <div v-else-if="estado === 'vivo'" class="tc-card tc-vivo">
      <div class="tc-main">
        <div class="tc-head">
          <span class="tc-avatar">🎟️</span>
          <span class="tc-chip">Golden ticket</span>
        </div>
        <h3 class="tc-title">
          Te regalamos <strong>{{ usosLabel }} de {{ servicio.nombre }}</strong>
        </h3>
        <p class="tc-sub">
          {{ servicio.bajada }} Inicia sesión y los sumamos a tu cuenta al
          tiro.
        </p>
        <div class="tc-badges">
          <span class="tc-badge">🎁 {{ usosLabel }} para ti</span>
          <span class="tc-badge" v-if="vence">Vence el {{ vence }}</span>
        </div>
      </div>
      <div class="tc-cta">
        <button type="button" class="tc-btn" @click="pedirSesion">
          Iniciar sesión y recibir mi regalo
        </button>
        <p class="tc-hint">Con tu cuenta de Google · sin contraseñas</p>
      </div>
    </div>

    <!-- Esta cuenta ya lo había cobrado: no es un error, es un recordatorio. -->
    <div v-else-if="estado === 'ya_canjeado'" class="tc-card tc-gris">
      <div class="tc-main">
        <h3 class="tc-title">Ya recibiste este regalo 🎟️</h3>
        <p class="tc-sub tc-sub-fin">
          Los {{ usosLabel }} de {{ servicio.nombre }} ya están en tu cuenta. El
          regalo es uno por cuenta: compártelo con tus colegas para que
          reciban los suyos.
        </p>
      </div>
    </div>

    <div v-else class="tc-card tc-gris">
      <div class="tc-main">
        <h3 class="tc-title">¡Bienvenido/a a <strong>Utopie</strong>!</h3>
        <p class="tc-sub tc-sub-fin">{{ explicacion }}</p>
      </div>
    </div>
  </b-container>
</template>

<script>
// Tarjeta de quien llega por el enlace de un código promocional
// (`utopie.cl/d/<codigo>`, el QR de un brochure o de un video, que Django
// redirige a la demo del servicio con `?promo=<codigo>`).
//
// El código es un PromotionalCode, el mismo de las misiones: lo canjea
// `redeem_promotional_code`, una vez por cuenta (405 si la cuenta ya lo usó), y
// si el código está ligado a una misión, el canje también la completa.
//
// Calca el recorrido de TicketBanner: el código se aparca en localStorage
// apenas se lee, así sobrevive al ida y vuelta del login de Google, y el canje
// se dispara solo en cuanto hay sesión.
import userService from "@/services/userService";
import { eventoLogin, medirCodigoCanjeado } from "@/services/analytics";
import { recordarOrigenLogin } from "@/services/origen";
import { marcarTicketVisible } from "./ticketFocus";

const STORAGE_KEY = "promo_code";

// De dónde salió este muro de login, para el embudo de GA4.
const ORIGEN_LOGIN = "codigo_promocional";

const guardar = (valor) => {
  try {
    localStorage.setItem(STORAGE_KEY, valor);
  } catch (e) {
    /* sin almacenamiento el código no sobrevive al login, pero la página sí */
  }
};
const leer = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) || "";
  } catch (e) {
    return "";
  }
};
const olvidarGuardado = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* nada que limpiar */
  }
};

// Se captura al CARGAR EL MÓDULO, no en `created()`: una sesión vencida empuja
// a la portada antes de que la tarjeta se monte y se lleva la query consigo
// (ver el mismo comentario en TicketBanner).
const codigoDeLaUrl = () => {
  const crudo =
    String(window.location.hash || "") + String(window.location.search || "");
  // El `?` también corta. `crudo` pega el hash con la query real, y el
  // redirect pone las utm en la query: sin el `?` en la clase, el código
  // se leía como `CODIGO?utm_source=qr` y el servidor respondía «no existe».
  const m = /[?&]promo=([^&#?]+)/.exec(crudo);
  if (!m) return "";
  try {
    return decodeURIComponent(m[1]);
  } catch (e) {
    return m[1];
  }
};
const CODIGO_INICIAL = codigoDeLaUrl();
if (CODIGO_INICIAL) guardar(CODIGO_INICIAL);

const CONFETTI_COLORS = [
  "#20b2aa", "#84cec7",
  "#c6588d", "#e8b5d1",
  "#f8ac04", "#f9d38f",
  "#e84756", "#ecd5cd",
];

// Qué mutación del store refleja cada campo de usos. El saldo del store solo se
// refresca al iniciar sesión o por el socket, y el socket se conecta al mismo
// tiempo que la tarjeta cobra: puede llegar antes del canje o no llegar. Por
// eso el canje devuelve los saldos de la base y se escriben tal cual, sin sumar
// (sumar encima de un socket que ya trajo el saldo nuevo duplicaba el regalo).
const MUTACION_DE_USOS = {
  documents_uses: "login/setDocumentsUses",
  tests_uses: "login/setTestsUses",
  planifications_uses: "login/setPlanificationsUses",
  materials_uses: "login/setMaterialsUses",
};

// Cómo se nombra lo que regala el código, según el campo de usos.
const SERVICIOS = {
  documents_uses: {
    nombre: "documentos PIE",
    bajada: "Informes a la familia, FUR, anamnesis y más, listos para descargar.",
    cierre: "Ya puedes empezar a crearlos.",
  },
  tests_uses: {
    nombre: "corrección de tests",
    bajada: "Corrige tus tests y baja el informe listo en Word.",
    cierre: "Ya puedes empezar a corregir.",
  },
  planifications_uses: {
    nombre: "planificaciones",
    bajada: "Planificaciones de clase listas para usar.",
    cierre: "Ya puedes empezar a planificar.",
  },
  materials_uses: {
    nombre: "materiales de clase",
    bajada: "Materiales de clase listos para imprimir.",
    cierre: "Ya puedes empezar a crearlos.",
  },
};
const SERVICIO_GENERICO = {
  nombre: "Utopie",
  bajada: "",
  cierre: "Ya puedes usarlos.",
};

// Con cuánta anticipación se avisa el vencimiento en la tarjeta.
const DIAS_PARA_AVISAR = 60;

const EXPLICACION = {
  vencido: "Este regalo ya venció.",
  invalido:
    "No encontramos este regalo: puede que el enlace esté incompleto o que ya no exista.",
};

export default {
  name: "PromoCodeBanner",
  data() {
    return {
      codigo: "",
      estado: "",
      usos: 0,
      campo: "",
      vence: "",
      celebrando: false,
      cobrando: false,
      confetti: [],
    };
  },
  created() {
    const codigo =
      CODIGO_INICIAL || this.$route.query.promo || leer();
    if (!codigo) return;
    guardar(codigo);
    this.codigo = codigo;
    this.consultar();
  },
  watch: {
    "$store.state.login.token"(value) {
      if (value) {
        // Inició sesión con la tarjeta en pantalla. Antes de cobrar se vuelve
        // a preguntar: esta cuenta puede ser una que ya cobró.
        this.consultar();
        return;
      }
      // Cerró sesión: la celebración era de la cuenta anterior.
      if (this.celebrando && this.codigo) {
        this.celebrando = false;
        guardar(this.codigo);
        this.consultar();
      }
    },
  },
  computed: {
    cuenta() {
      const s = this.$store.state.login;
      return s.email || s.first_name || "";
    },
    explicacion() {
      return EXPLICACION[this.estado] || EXPLICACION.invalido;
    },
    servicio() {
      return SERVICIOS[this.campo] || SERVICIO_GENERICO;
    },
    usosLabel() {
      return `${this.usos} ${this.usos === 1 ? "uso" : "usos"}`;
    },
  },
  beforeDestroy() {
    marcarTicketVisible(false);
  },
  methods: {
    consultar() {
      userService.promotionalCodeInfo(this.codigo).then(
        (data) => {
          this.estado = data.estado || "invalido";
          // Lo que se muestra es la primera recompensa con un servicio
          // conocido; un código de misión puede traer varias.
          const rewards = data.rewards || [];
          const principal =
            rewards.find((r) => SERVICIOS[r.field]) || rewards[0] || {};
          this.usos = Number(principal.uses) || 0;
          this.campo = principal.field || "";
          this.vence = this.venceCercano(data.vence);
          if (this.estado !== "vivo") {
            marcarTicketVisible(false);
            olvidarGuardado();
            return;
          }
          if (!this.$store.state.login.token) {
            eventoLogin("login_wall_shown", { origen_login: ORIGEN_LOGIN });
          }
          marcarTicketVisible(true);
          this.$nextTick(() => this.mostrarTarjeta());
          this.cobrar();
        },
        // Si la consulta falla no se inventa un estado.
        () => this.olvidar()
      );
    },
    pedirSesion() {
      // Otra vez al apretar: cerrar cualquier otro modal borra el origen.
      recordarOrigenLogin(ORIGEN_LOGIN);
      this.$emit("open-login");
    },
    olvidar() {
      marcarTicketVisible(false);
      olvidarGuardado();
      this.codigo = "";
      this.estado = "";
    },
    mostrarTarjeta() {
      const el = this.$el && this.$el.querySelector(".tc-card");
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
    // El vencimiento solo se muestra si está cerca. El admin exige una fecha
    // para los códigos promocionales, así que uno «sin vencimiento» se crea con
    // una fecha lejana, y «Vence el 31-12-2030» no le dice nada útil a nadie.
    venceCercano(iso) {
      if (!iso) return "";
      const dias = (new Date(iso + "T23:59:59") - new Date()) / 86400000;
      return dias <= DIAS_PARA_AVISAR ? this.fechaHumana(iso) : "";
    },
    fechaHumana(iso) {
      if (!iso) return "";
      const [a, m, d] = String(iso).split("-");
      if (!a || !m || !d) return "";
      return `${Number(d)}-${Number(m)}-${a}`;
    },
    cobrar() {
      if (this.celebrando || this.cobrando) return;
      if (!this.codigo || this.estado !== "vivo") return;
      if (!this.$store.state.login.token) return;
      this.cobrando = true;
      userService.redeemPromotionalCode({ code: this.codigo }).then(
        (data) => {
          this.cobrando = false;
          marcarTicketVisible(false);
          olvidarGuardado();
          this.fijarSaldos(data && data.saldos);
          medirCodigoCanjeado(this.usos, this.campo);
          this.confetti = this.armarConfetti();
          this.celebrando = true;
        },
        (error) => {
          this.cobrando = false;
          marcarTicketVisible(false);
          olvidarGuardado();
          const status = error.response && error.response.status;
          // 405 esta cuenta ya lo usó · 410 venció · 404 no existe. Otra cosa
          // es un error nuestro y no se dice nada que pueda ser falso.
          if (status === 405) this.estado = "ya_canjeado";
          else if (status === 410) this.estado = "vencido";
          else if (status === 404) this.estado = "invalido";
          else this.olvidar();
        }
      );
    },
    fijarSaldos(saldos) {
      Object.keys(MUTACION_DE_USOS).forEach((campo) => {
        if (saldos && saldos[campo] != null) {
          this.$store.commit(MUTACION_DE_USOS[campo], Number(saldos[campo]));
        }
      });
    },
    // El mismo confeti de TicketBanner (portado de TransactionSuccess.vue).
    armarConfetti() {
      const piezas = [];
      for (let i = 0; i < 80; i += 1) {
        const tam = 6 + Math.random() * 8;
        const forma = Math.random();
        piezas.push({
          left: (Math.random() * 100).toFixed(2) + "%",
          color:
            CONFETTI_COLORS[
              Math.floor(Math.random() * CONFETTI_COLORS.length)
            ],
          ancho: tam.toFixed(1) + "px",
          alto: (tam * 1.4).toFixed(1) + "px",
          radio: forma < 0.33 ? "50%" : forma < 0.66 ? "2px" : "0",
          giro:
            (Math.random() < 0.5 ? -1 : 1) *
              Math.floor(360 + Math.random() * 720) +
            "deg",
          delay: (0.1 + Math.random() * 2).toFixed(2) + "s",
          dur: (3 + Math.random() * 2.5).toFixed(2) + "s",
        });
      }
      return piezas;
    },
  },
};
</script>

<style lang="scss" scoped>
.tc-wrap {
  margin-bottom: 1.5rem;
}
.tc-card {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.5rem;
  border-radius: 18px;
  padding: 1.5rem;
  text-align: left;
  overflow: hidden;
}
.tc-vivo {
  border: 2px dashed #f5a623;
  background: #fffaf0;
}
.tc-win {
  border: 2px solid #f5a623;
  background: #fffaf0;
  text-align: center;
}
.tc-gris {
  border: 2px solid #e3e3e0;
  background: #f7f7f5;
}
.tc-main {
  flex: 1 1 320px;
  position: relative;
  z-index: 1;
}
.tc-head {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.75rem;
}
.tc-avatar {
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 50%;
  background: #f5a623;
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tc-chip {
  border: 1px solid #f5a623;
  border-radius: 999px;
  padding: 0.15rem 0.75rem;
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9a6208;
}
.tc-title {
  font-weight: 400;
  margin-bottom: 0.5rem;
  strong {
    color: #9a6208;
  }
}
.tc-sub {
  margin-bottom: 0.75rem;
}
.tc-sub-fin {
  margin-bottom: 0;
}
.tc-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.tc-badge {
  border: 1px solid #f0dfbe;
  border-radius: 10px;
  padding: 0.35rem 0.75rem;
  background: white;
  font-size: 0.9rem;
}
.tc-cta {
  flex: 0 1 320px;
  text-align: center;
  position: relative;
  z-index: 1;
}
.tc-btn {
  width: 100%;
  border: 1px solid #f5a623;
  border-radius: 12px;
  background: white;
  color: #9a6208;
  font-weight: 700;
  padding: 0.9rem 1rem;
}
.tc-hint {
  font-size: 0.8rem;
  color: #7b756c;
  margin: 0.5rem 0 0;
}
.tc-emoji {
  font-size: 2.4rem;
  line-height: 1;
  margin-bottom: 0.4rem;
}
.tc-lluvia {
  position: fixed;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 1040;
}
.tc-piece {
  position: absolute;
  top: -20px;
  opacity: 0;
  animation-name: tc-fall;
  animation-timing-function: linear;
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
}
@keyframes tc-fall {
  0% {
    opacity: 1;
    transform: translateY(0) rotateZ(0deg) rotateX(0deg);
  }
  100% {
    opacity: 0.85;
    transform: translateY(110vh) rotateZ(var(--tc-giro, 720deg)) rotateX(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .tc-lluvia {
    display: none;
  }
}
</style>
